import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { notifText } from "@/lib/notifText";

const RINGS = ["#F59E0B", "#10B981", "#3B82F6", "#EC4899", "#8B5CF6", "#06B6D4", "#EF4444", "#84CC16"];
const ringFor = (id) => RINGS[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % RINGS.length];
const initials = (n) => (n || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export function useLive(uid, me, enabled) {
  const qc = useQueryClient();
  const loc = useLocation();
  const [users, setUsers] = useState([]);
  const wsRef = useRef(null);
  const tab = loc.pathname.split("/")[3] || "";

  useEffect(() => {
    if (!enabled) return;
    let closed = false, timer;
    const connect = async () => {
      let token = "";
      try {
        token = (await api.get("/auth/ws-token")).data.token;
      } catch {
        if (!closed) timer = setTimeout(connect, 5000);
        return;
      }
      if (closed) return;
      const url = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/${uid}?token=${token}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: "tab", tab: window.location.pathname.split("/")[3] || "" }));
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "presence") setUsers(msg.users);
        if (msg.type === "notification" && msg.user_ids?.includes(me?.id)) {
          qc.invalidateQueries({ queryKey: ["notifications"] });
          toast(`${msg.from} ${notifText(msg.kind)} on ${msg.entity_name}`, {
            description: msg.snippet, duration: 8000,
            action: { label: "Open", onClick: () => { window.location.assign(`/u/${uid}/lore/${msg.entity_id}?comment=${msg.comment_id}`); } },
          });
        }
        if (msg.type === "change") {
          qc.invalidateQueries();
          if (msg.by_id && msg.by_id !== me?.id) {
            window.dispatchEvent(new CustomEvent("lf-change", { detail: msg }));
            if (msg.label) toast(`${msg.by} ${msg.label}`, { duration: 2500 });
          }
        }
      };
      ws.onclose = () => { if (!closed) timer = setTimeout(connect, 3000); };
    };
    connect();
    return () => { closed = true; clearTimeout(timer); wsRef.current?.close(); };
  }, [uid, enabled, me?.id, qc]);

  useEffect(() => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: "tab", tab }));
  }, [tab]);
  return users;
}

export const PresenceBar = ({ users, me }) => (
  <div className="flex items-center gap-1.5 px-5 py-3 border-b border-[#2D3442]" data-testid="presence-bar">
    <span className="lf-label mr-1">Here now</span>
    <AnimatePresence>
      {users.map((u) => (
        <motion.span key={u.id} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
          title={`${u.name}${u.id === me?.id ? " (you)" : ""} · ${u.tab || "dashboard"}`} data-testid={`presence-user-${u.id}`}
          className="relative flex h-7 w-7 items-center justify-center rounded-full bg-[#1A202C] text-[10px] font-semibold text-[#F3EBDD]"
          style={{ boxShadow: `0 0 0 2px ${ringFor(u.id)}` }}>
          {initials(u.name)}
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-sm bg-[#0B0D10] px-1 text-[7px] uppercase tracking-wider text-[#A8A090]">{(u.tab || "").slice(0, 4)}</span>
        </motion.span>
      ))}
    </AnimatePresence>
  </div>
);
